import React from 'react';
import { Modal, Typography, Space, Card, List, Table } from 'antd';
import { FormulaExplanation, GradeScale } from '../types';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;

interface ScoreExplanationModalProps {
	visible: boolean;
	onClose: () => void;
	explanation: FormulaExplanation;
	title: string;
}

export const ScoreExplanationModal: React.FC<ScoreExplanationModalProps> = ({
	visible,
	onClose,
	explanation,
	title,
}) => {
	const gradeColumns = [
		{
			title: 'Grade',
			dataIndex: 'grade',
			key: 'grade',
			render: (grade: string, record: GradeScale) => (
				<Text strong style={{ color: record.color }}>
					{grade}
				</Text>
			),
		},
		{
			title: 'Percentile',
			dataIndex: 'percentile',
			key: 'percentile',
			render: (percentile: number) => `${percentile}th`,
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
		},
	];

	return (
		<Modal title={title} open={visible} onCancel={onClose} footer={null} width={800}>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				{/* Overview Section */}
				<Card>
					<Title level={5}>Overview</Title>
					<Paragraph>{explanation.explanation}</Paragraph>
				</Card>

				{/* Formula Section */}
				<Card>
					<Title level={5}>Score Formula</Title>
					<Text code style={{ fontSize: '16px', marginBottom: '16px', display: 'block' }}>
						{explanation.formula}
					</Text>
					<List
						size="small"
						dataSource={explanation.components}
						renderItem={(item) => (
							<List.Item>
								<Space>
									<Text code>{item.variable}</Text>
									<Text>-</Text>
									<Text>{item.description}</Text>
								</Space>
							</List.Item>
						)}
					/>
				</Card>

				{/* Grading Scale Section */}
				<Card>
					<Title level={5}>Grading Scale</Title>
					<Paragraph>
						<InfoCircleOutlined /> Grades are assigned based on percentile rankings within the team
					</Paragraph>
					<Table
						dataSource={explanation.gradeScale}
						columns={gradeColumns}
						pagination={false}
						size="small"
					/>
				</Card>

				{/* Tips Section */}
				<Card>
					<Title level={5}>Tips for Improvement</Title>
					<List
						size="small"
						dataSource={explanation.tips}
						renderItem={(tip) => (
							<List.Item>
								<Text>• {tip}</Text>
							</List.Item>
						)}
					/>
				</Card>
			</Space>
		</Modal>
	);
};
